import mapValues from 'lodash.mapvalues';
import flow from 'lodash.flow';
import orderBy from 'lodash.orderby';
import head from 'lodash.head';
import get from 'lodash.get';

/* eslint-disable import/no-extraneous-dependencies */

import map from 'lodash/fp/map';
import flatten from 'lodash/fp/flatten';
import groupBy from 'lodash/fp/groupBy';
import values from 'lodash/fp/values';

/* eslint-enable import/no-extraneous-dependencies */

import { deepCamelCase } from '@amplify/web-assessment-common/dist/utils';
import { DIBELS8, R3D, BURST, R3DSPANISH, IDEL2, OLS } from '../../constants/applications';
import {
    SKILL_REPLACEMENT_MAP,
    SKILL_REPLACEMENT_MAP_MEASURES,
    MEASURE_BY_MEASURE_SCORE,
    OPTIONAL_MEASURE_SCORES,
} from '../../constants/assessments';
import { calculateCompositeForStudent } from './calculateCompositeScores';
import { BM_PERIODS, PERIOD } from '../../constants/enrollment';
import getRequiredPlacementTestMeasures from './calculatePlacementStatus';
import { getValidOfficialBurstResults } from './studentsListHelpers';

/**
 * Dibels8 results for period
 * @typedef {Object} PeriodDibels8Results
 * @property {Object.<string, object>} - results in period by student id
 *
 * @example
 * {
 *     studentId1: { result },
 *     studentId2: { result },
 * }
 */

/**
 * Dibels8 results object
 * @typedef {Object} Dibels8Results
 * @property {PeriodDibels8Results} BOY - dibels8 results in BOY period
 * @property {PeriodDibels8Results} MOY - dibels8 results in MOY period
 * @property {PeriodDibels8Results} EOY - dibels8 results in EOY period
 *
 * @example
 * {
 *     BOY: { studentId1: { BOY result }, studentId2: { BOY result } },
 *     MOY: { studentId1: { MOY result }, studentId2: { MOY result } },
 *     EOY: { studentId1: { EOY result }, studentId2: { EOY result } },
 * }
 */

/**
 * Apply provided function to every student results array.
 * @param {Dibels8Results} results - class results (classesStudents[classId].results)
 * @param {Object[]} students - class students (from classesStudents[classId].students)
 * @param {function} mapFunction - function for processing every student results array
 */
const mapResults = (results, students, mapFunction) =>
    mapValues(results, (resultsByPeriod, period) =>
        mapValues(resultsByPeriod, (studentResults, studentId) => {
            const grade = get(
                students.find(student => student.id === studentId),
                'grade',
            );
            return mapFunction(studentResults, grade, period);
        }),
    );

const pickMostOfficialResults = ({ students, results }, appName) =>
    mapResults(results[appName], students, (studentResults, _grade, period) =>
        period === PERIOD.PM
            ? studentResults
            : flow([
                  groupBy('measure'),
                  map(measureResults =>
                      head(
                          orderBy(measureResults, ['isValid', 'officialnessRank'], ['desc', 'asc']),
                      ),
                  ),
                  values,
                  flatten,
              ])(studentResults),
    );

const getResultsForStudentGrade = ({ students, results }, appName) =>
    mapResults(results[appName], students, (studentResults, grade) =>
        // OLS results are different from other results, they have "grade" instead of "gradeLevel"
        studentResults.filter(result => (result.gradeLevel || result.grade) === grade),
    );

const getResultsForLanguage = ({ students, results, language }, appName) =>
    mapResults(results[appName], students, studentResults =>
        studentResults.filter(result => result.language === language),
    );

const getValidResults = ({ students, results }, appName) =>
    mapResults(results[appName], students, studentResults =>
        studentResults.filter(result => result.isValid),
    );

const filterOptionalMeasures = (studentResults, grade, appName) =>
    studentResults.filter(
        studentResult =>
            !get(OPTIONAL_MEASURE_SCORES[appName], grade, [])
                .map(skillName => MEASURE_BY_MEASURE_SCORE[skillName])
                .includes(studentResult.measure),
    );

const calculateCompositeScoresForClass = ({ students, results, gatingRulesConfig }, appName) =>
    mapResults(results[appName], students, (studentResults, grade, period) =>
        period === PERIOD.PM
            ? studentResults
            : calculateCompositeForStudent(
                  filterOptionalMeasures(studentResults, grade, appName),
                  appName,
                  grade,
                  period,
                  gatingRulesConfig,
              ),
    );

const getValidOfficialBurstResultsByPeriodStudent = ({ students, results }, _appName) =>
    mapResults(results[BURST] || {}, students, (studentResults, _grade, period) =>
        getValidOfficialBurstResults(studentResults, period),
    );

const calculatePlacementTestStatusForClass = (
    { students, appStatuses, results },
    bmWindows,
    currentPeriod,
) =>
    mapValues(appStatuses[BURST], (statusByPeriod, period) =>
        mapValues(statusByPeriod, (studentStatus, studentId) => {
            if (period === currentPeriod) {
                const gatedMeasureStatuses = BM_PERIODS.reduce((statuses, bmPeriod) => {
                    return {
                        ...statuses,
                        [bmPeriod]: get(
                            appStatuses.dibels8,
                            `${bmPeriod}.${studentId}.gatedMeasuresStatus`,
                            {},
                        ),
                    };
                }, {});
                const requiredPlacementTestMeasures = getRequiredPlacementTestMeasures(
                    bmWindows,
                    currentPeriod,
                    get(statusByPeriod, `${PERIOD.PM}.${studentId}.instruction`),
                    gatedMeasureStatuses,
                    students.find(student => student.id === studentId),
                    results.burst,
                    results.dibels8,
                );
                const isPlacementTestComplete =
                    requiredPlacementTestMeasures !== null &&
                    requiredPlacementTestMeasures.length === 0;
                return {
                    ...studentStatus,
                    isPlacementTestComplete,
                    requiredPlacementTestMeasures,
                };
            }
            return {
                ...studentStatus,
                isPlacementTestComplete: null,
                requiredPlacementTestMeasures: [],
            };
        }),
    );

const trasnformResultsToCamelCase = ({ students, results }, appName) =>
    mapResults(results[appName], students, studentResults => studentResults.map(deepCamelCase));

const applyFuncToClassDataPart = (fieldName, appNames, func) => classData => ({
    ...classData,
    [fieldName]: {
        ...classData[fieldName],
        ...Object.keys(classData[fieldName]).reduce((classDataPartByApp, appName) => {
            return appNames.includes(appName)
                ? {
                      ...classDataPartByApp,
                      [appName]: func(classData, appName),
                  }
                : classDataPartByApp;
        }, {}),
    },
});

export const prepareBurstClassData = (classData, bmWindows, bmPeriod) => ({
    ...classData,
    appStatuses: {
        ...classData.appStatuses,
        [BURST]: calculatePlacementTestStatusForClass(classData, bmWindows, bmPeriod),
    },
});

export const replaceSkillNames = ({ students, results }, appName) =>
    mapResults(results[appName], students, studentResults =>
        studentResults.map(result => {
            const { measureScores, measure: measureName } = result;
            if (!SKILL_REPLACEMENT_MAP_MEASURES.includes(measureName)) {
                return result;
            }
            const updatedSkillNames = Object.keys(measureScores).reduce(
                (scoresToUpdate, measure) => {
                    const replaceableSkills = get(SKILL_REPLACEMENT_MAP, measure);
                    if (
                        replaceableSkills &&
                        measureScores[measure].skillName === replaceableSkills.fromSkillName
                    ) {
                        return {
                            ...scoresToUpdate,
                            [measure]: {
                                ...measureScores[measure],
                                skillName: replaceableSkills.toSkillName,
                            },
                        };
                    }
                    return scoresToUpdate;
                },
                {},
            );

            return {
                ...result,
                measureScores: {
                    ...result.measureScores,
                    ...updatedSkillNames,
                },
            };
        }),
    );

export const prepareClassData = flow([
    applyFuncToClassDataPart('results', [DIBELS8, IDEL2, OLS], getResultsForStudentGrade),
    applyFuncToClassDataPart('results', [OLS], getValidResults),
    applyFuncToClassDataPart('results', [OLS], getResultsForLanguage),
    applyFuncToClassDataPart('results', [DIBELS8, IDEL2, OLS], pickMostOfficialResults),
    applyFuncToClassDataPart('appStatuses', [DIBELS8, IDEL2], calculateCompositeScoresForClass),
    applyFuncToClassDataPart('results', [BURST], getValidOfficialBurstResultsByPeriodStudent),
    applyFuncToClassDataPart('results', [IDEL2], replaceSkillNames),
]);

export const formatResults = applyFuncToClassDataPart(
    'results',
    [R3D, BURST, R3DSPANISH],
    trasnformResultsToCamelCase,
);

/**
 * Transform assessment data for selected class that comes from different sources,
 * so it's ready for underlying components consumption.
 * @param {Object} selectedClassesStudents part of classeStudents data for selected class, coming from props
 * @param {Object} resultsFromStore results map for students in class, coming from browser storage
 * @param {String} language current language
 * @returns {Object} { allFormattedResults - all grouped student results, including unofficial, off-grade etc.
 *                     selectedClassData - class data with results that contributed to composite
 *                   }
 */
export const prepareResultsClassData = (
    selectedClassesStudents,
    resultsFromStore = {},
    language,
    gatingRulesConfig,
) => {
    const classeStudentsWithAllResults = {
        ...selectedClassesStudents,
        results: { ...selectedClassesStudents.results, ...resultsFromStore },
    };
    const allFormattedResults = formatResults(classeStudentsWithAllResults);
    allFormattedResults.language = language;
    allFormattedResults.gatingRulesConfig = gatingRulesConfig;

    return {
        allFormattedResults,
        selectedClassData: prepareClassData(allFormattedResults),
    };
};
